import "./Logo.css";

/**
 * The Emporia logo, at a size a browser should actually download.
 *
 * It replaced eight call sites that each loaded the full source logo, which is
 * 2,066 KB at 948x1432 and rendered **44px tall** — about 29x44 CSS pixels in
 * the nav — so it was 87.5% of everything a first-time visitor downloaded,
 * for an image the size of a postage stamp. It was also the favicon and the
 * apple-touch-icon, and `manifest.json` declared it as a 192x192 PWA icon,
 * which it has never been at any point (it is not square, let alone 192px).
 *
 * The replacements in `public/brand/` are the same artwork, resized: nothing
 * is cropped, recoloured or redrawn, and the source has no transparent margin
 * to trim (its bounding box is the full canvas). The PNGs are quantised to
 * 256 colours, which is what takes them from 28 KB to 8 KB; the soft brush
 * edges need real alpha, so that is kept.
 *
 * ## Sizing
 *
 * 1x is 73px tall because that is the **tallest** place the logo renders —
 * the mosaic tile, which is width-constrained to 48px and so stands 73px
 * high. The nav and footer are 44px and auth is 56px, so no placement is
 * ever upscaled.
 *
 * ## Why `<picture>` and not just `srcSet`
 *
 * A real WebP fallback needs `<source type="image/webp">`; `srcSet` alone
 * cannot express "this format, or else that one". Only Safari 13 and older
 * lack WebP, which is why there is **one** PNG fallback rather than three —
 * a 3x asset for a browser nobody on a 3x screen is running would be bytes
 * in the repo that are never fetched.
 *
 * `<picture>` is `display: contents` (see Logo.css) so it adds no box of its
 * own: every one of those eight call sites sits in a flex or grid parent that
 * was laying out the `<img>` directly, and an extra inline element between
 * them would shift the logo. The `<img>` keeps the caller's className, so
 * every existing rule — `.nav-logo`, `.footer__logo`, `.auth-logo`,
 * `.admin-brand-logo`, `.mosaic-back-logo` — still applies unchanged.
 */
const Logo = ({ className, alt = "Emporia", ...rest }) => (
  <picture className="logo-picture">
    <source
      type="image/webp"
      srcSet="/brand/logo.webp 1x, /brand/logo@2x.webp 2x, /brand/logo@3x.webp 3x"
    />
    <img src="/brand/logo.png" alt={alt} className={className} {...rest} />
  </picture>
);

export default Logo;
